import { Box, Button, Input } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import {
  formatDealIrrMonthInputValue,
  isDealIrrFirstMarketingMonthOverridden,
  resetDealIrrTableFirstMarketingMonth,
  resolveDealIrrTableFirstMarketingMonth,
  resolveDealIrrTableMonthlyIrrDisplay,
  resolveDealIrrTableRoasDisplay,
  updateDealIrrTableFirstMarketingMonth,
  updateDealIrrTableMarketing,
  updateDealIrrTableMonthlyIrr,
  updateDealIrrTableRoas,
} from './calc/dealIrrTableEdits';
import { fmtIntegerInput, parseIntegerInput, fmtMoneyDebug } from './calc/formatters';

function formatPctPoints(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return '—';
  }
  return `${Number(value).toFixed(2)}%`;
}

function formatPctDecimal(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return '—';
  }
  return `${(Number(value) * 100).toFixed(3)}%`;
}

function formatCalendarMonth(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function TableCell({
  value,
  format = 'money',
  highlight = false,
  editable = false,
  sticky = false,
  editMode = false,
  editValue,
  onEditChange,
  editLabel,
}) {
  const numeric = Number(value) || 0;
  let display = '—';
  if (format === 'label') {
    display = value != null && value !== '' ? String(value) : '';
  } else if (value != null && (numeric !== 0 || editable)) {
    if (format === 'pctPoints') {
      display = formatPctPoints(numeric);
    } else if (format === 'pctDecimal') {
      display = formatPctDecimal(numeric);
    } else if (format === 'month') {
      display = formatCalendarMonth(value);
    } else {
      display = fmtMoneyDebug(numeric, 0);
    }
  }

  if (editMode && onEditChange) {
    const inputValue =
      format === 'money'
        ? fmtIntegerInput(editValue ?? value ?? 0)
        : editValue ?? value ?? 0;

    return (
      <Box
        as="td"
        className={`deal-irr-cell deal-irr-cell--editable${
          sticky ? ' deal-irr-sticky-col' : ''
        }${highlight ? ' deal-irr-cell--section' : ''}`}
      >
        <Input
          aria-label={editLabel}
          className="deal-irr-cell-input"
          size="xs"
          type={format === 'money' ? 'text' : 'number'}
          inputMode={format === 'money' ? 'numeric' : 'decimal'}
          min={format === 'pctPoints' ? 0 : undefined}
          step={format === 'pctPoints' ? 0.01 : undefined}
          textAlign="right"
          value={inputValue}
          onChange={(e) => {
            if (format === 'money') {
              onEditChange(parseIntegerInput(e.target.value));
              return;
            }
            onEditChange(Number(e.target.value) || 0);
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      as="td"
      className={`deal-irr-cell${sticky ? ' deal-irr-sticky-col' : ''}${
        highlight ? ' deal-irr-cell--section' : ''
      }${editable ? ' deal-irr-cell--editable' : ''}`}
    >
      {display}
    </Box>
  );
}

TableCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
  format: PropTypes.string,
  highlight: PropTypes.bool,
  editable: PropTypes.bool,
  sticky: PropTypes.bool,
  editMode: PropTypes.bool,
  editValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onEditChange: PropTypes.func,
  editLabel: PropTypes.string,
};

function DealIrrModelTable({
  model,
  useHillRoas,
  editMode = false,
  state,
  setState,
  title = 'Pricing Model',
}) {
  if (!model) {
    return null;
  }

  const { monthCount, calendarMonths } = model;
  const firstMarketingMonth = state
    ? resolveDealIrrTableFirstMarketingMonth(state)
    : calendarMonths[0];
  const firstMarketingMonthOverridden =
    editMode && state ? isDealIrrFirstMarketingMonthOverridden(state) : false;
  const planMonthCount = state?.marketing?.length ?? 0;
  const curveLabel = useHillRoas ? 'Max ROAS (incremental)' : 'Cumulative ROAS';
  const incrementalLabel = 'Incremental ROAS';

  const rows = [
    {
      key: 'irr',
      label: 'IRR — fee % / month',
      values: model.monthlyIrrRow,
      format: 'pctPoints',
      editableInEditMode: true,
    },
    {
      key: 'marketing',
      label: 'Marketing spend',
      values: model.marketing,
      editable: true,
      editableInEditMode: true,
    },
    { key: 'curve', label: curveLabel, values: model.curve, format: 'pctDecimal', editableInEditMode: !useHillRoas },
    ...(useHillRoas
      ? []
      : [{ key: 'incremental', label: incrementalLabel, values: model.incremental, format: 'pctDecimal' }]),
    { key: 'customerHeader', label: 'Customer cash', section: true },
    { key: 'customerMarketing', label: 'Marketing', values: model.customerMarketing },
    { key: 'customerGross', label: 'Gross revenue', values: model.customerGross },
    { key: 'customerNet', label: 'Net revenue', values: model.customerNet },
    {
      key: 'twelveFundingPct',
      label: 'Twelve funding %',
      values: model.twelveFundingPct,
      format: 'pctDecimal',
    },
    { key: 'customerFunding', label: 'Twelve funding', values: model.customerFunding },
    { key: 'customerRepayment', label: 'Twelve repayment', values: model.customerRepayment },
    { key: 'customerFees', label: 'Twelve fees', values: model.customerFees },
    { key: 'customerNetCf', label: 'Net cash flow', values: model.customerNetCf },
    { key: 'twelveHeader', label: 'Twelve cash', section: true },
    { key: 'twelveOrigination', label: 'Origination (outflow)', values: model.twelveOrigination },
    { key: 'twelveRepayment', label: 'Repayment (inflow)', values: model.twelveRepayment },
    { key: 'twelveFee', label: 'Facility fee (inflow)', values: model.twelveFee },
    { key: 'twelveNet', label: 'Net cash flow', values: model.twelveNet },
    { key: 'interest', label: 'Monthly interest (accrued)', values: model.interest },
    { key: 'exposure', label: 'Exposure', values: model.exposure },
  ];

  const canEditRow = (row) =>
    Boolean(editMode && row.editableInEditMode && setState);

  return (
    <Box className="deal-irr-table-wrap" overflowX="auto">
      <Box as="table" className="deal-irr-table">
        <Box as="thead">
          <Box as="tr">
            <Box as="th" className="deal-irr-sticky-col">
              {title}
            </Box>
            {Array.from({ length: monthCount }, (_, index) => (
              <Box as="th" key={`head-${index}`}>
                {editMode && index === 0 && setState ? (
                  <Input
                    aria-label="First calendar month"
                    className="deal-irr-month-input"
                    size="xs"
                    type="month"
                    value={formatDealIrrMonthInputValue(firstMarketingMonth)}
                    onChange={(event) =>
                      updateDealIrrTableFirstMarketingMonth(setState, event.target.value)
                    }
                  />
                ) : (
                  formatCalendarMonth(calendarMonths[index])
                )}
              </Box>
            ))}
          </Box>
          <Box as="tr" className="deal-irr-subhead-row">
            <Box as="th" className="deal-irr-sticky-col">
              {editMode && firstMarketingMonthOverridden && setState ? (
                <Button
                  size="xs"
                  variant="link"
                  colorScheme="purple"
                  onClick={() => resetDealIrrTableFirstMarketingMonth(setState)}
                >
                  Reset month
                </Button>
              ) : null}
            </Box>
            {Array.from({ length: monthCount }, (_, index) => (
              <Box as="th" key={`sub-${index}`}>
                Month {index}
              </Box>
            ))}
          </Box>
        </Box>
        <Box as="tbody">
          {rows.map((row) =>
            row.section ? (
              <Box as="tr" key={row.key} className="deal-irr-section-row">
                <TableCell value={row.label} format="label" highlight sticky />
                {Array.from({ length: monthCount }, (_, index) => (
                  <TableCell key={`${row.key}-${index}`} value="" format="label" highlight />
                ))}
              </Box>
            ) : (
              <Box as="tr" key={row.key}>
                <TableCell value={row.label} format="label" sticky />
                {(row.values || []).slice(0, monthCount).map((value, index) => {
                  const isEditable = canEditRow(row);
                  let editValue;
                  let onEditChange;
                  let editLabel;

                  if (isEditable && row.key === 'marketing') {
                    editValue = state?.marketing?.[index] ?? value;
                    onEditChange = (next) =>
                      updateDealIrrTableMarketing(setState, index, next);
                    editLabel = `Marketing spend month ${index}`;
                  } else if (isEditable && row.key === 'irr' && index < planMonthCount) {
                    editValue = resolveDealIrrTableMonthlyIrrDisplay(state, index);
                    onEditChange = (next) =>
                      updateDealIrrTableMonthlyIrr(setState, index, next);
                    editLabel = `IRR % month ${index}`;
                  } else if (isEditable && row.key === 'curve') {
                    editValue = resolveDealIrrTableRoasDisplay(state, index, value);
                    onEditChange = (next) =>
                      updateDealIrrTableRoas(setState, index, next);
                    editLabel = `Cumulative ROAS month ${index}`;
                  }


                  const cellFormat =
                    isEditable && row.key === 'curve' ? 'pctPoints' : row.format;

                  return (
                    <TableCell
                      key={`${row.key}-${index}`}
                      value={value}
                      format={cellFormat}
                      editable={row.editable}
                      editMode={Boolean(onEditChange)}
                      editValue={editValue}
                      onEditChange={onEditChange}
                      editLabel={editLabel}
                    />
                  );
                })}
              </Box>
            )
          )}
        </Box>
      </Box>
    </Box>
  );
}

DealIrrModelTable.propTypes = {
  model: PropTypes.object,
  useHillRoas: PropTypes.bool,
  editMode: PropTypes.bool,
  state: PropTypes.object,
  setState: PropTypes.func,
  title: PropTypes.string,
};

export default DealIrrModelTable;
